import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from "@azure/msal-browser";

let pca: PublicClientApplication;
let scope = "";
let account: AccountInfo | null = null;

/**
 * Prepara MSAL con los datos de /api/config y recoge la vuelta del inicio de
 * sesión. Devuelve la cuenta si ya hay sesión en este navegador.
 */
export async function initAuth(): Promise<AccountInfo | null> {
  const res = await fetch("/api/config");
  if (!res.ok) throw new Error("No se ha podido cargar la configuración");
  const cfg: { tenantId: string; clientId: string; scope: string } = await res.json();
  scope = cfg.scope;
  pca = new PublicClientApplication({
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      redirectUri: `${location.origin}/`,
      postLogoutRedirectUri: `${location.origin}/`,
    },
    // La sesión se recuerda en el móvil entre visitas.
    cache: { cacheLocation: "localStorage" },
  });
  await pca.initialize();
  const result = await pca.handleRedirectPromise();
  account = result?.account ?? pca.getAllAccounts()[0] ?? null;
  if (account) pca.setActiveAccount(account);
  return account;
}

export function signIn(): Promise<void> {
  return pca.loginRedirect({ scopes: [scope] });
}

export function signOut(): Promise<void> {
  return pca.logoutRedirect({ account });
}

/** Token de acceso para el backend; se renueva en silencio mientras la sesión siga viva. */
export async function getToken(): Promise<string> {
  if (!account) {
    await signIn();
    throw new Error("Inicia sesión para llamar");
  }
  try {
    return (await pca.acquireTokenSilent({ scopes: [scope], account })).accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await pca.acquireTokenRedirect({ scopes: [scope], account });
    }
    throw err;
  }
}

/** Traduce los errores de Entra más habituales a un mensaje para el comercial. */
export function loginErrorMessage(err: unknown): string {
  const e = err as { errorCode?: string; errorMessage?: string; message?: string };
  const text = `${e?.errorCode ?? ""} ${e?.errorMessage ?? e?.message ?? String(err)}`;
  const code = text.match(/AADSTS\d+/)?.[0];
  const known: Record<string, string> = {
    AADSTS50105: "Tu usuario no tiene acceso a esta aplicación. Pide que te añadan al grupo de comerciales.",
    AADSTS65001: "Falta el consentimiento de la aplicación. Avisa al administrador.",
    AADSTS50011: "La dirección de la web no coincide con la URI de redirección registrada en Entra.",
    AADSTS9002326: "En Entra, la URI de redirección tiene que ser de tipo «Aplicación de página única (SPA)».",
    AADSTS650053: "El permiso «llamar» no existe en la aplicación de Entra (Exponer una API).",
    AADSTS70011: "El permiso «llamar» no existe en la aplicación de Entra (Exponer una API).",
    AADSTS500011: "Falta el URI de id. de aplicación (api://…) en «Exponer una API».",
  };
  if (code && known[code]) return `${known[code]} (${code})`;
  const detail = code ?? e?.errorCode ?? "";
  return `No se ha podido iniciar sesión${detail ? ` (${detail})` : ""}. Vuelve a intentarlo.`;
}
